import React, { useEffect, useMemo, useRef, useState } from "react";
import { getImagenUrls, getMediaUrl } from "../../../utils/api";
import SectionTitleIcon from "../menu/SectionTitleIcon";
import SgcFiltrosHeader from "../filtros/SgcFiltrosHeader";
import SgcFiltroSection from "../filtros/SgcFiltroSection";
import SgcFiltrosFooter from "../filtros/SgcFiltrosFooter";
import { useSgcCardFiltersPanelHeight } from "../../../hooks/useSgcFiltersPanelHeight";
import "../filtros/SgcFiltrosSidebar.css";
import "./VolcanCartografiaHistorica.css";

export default function VolcanCartografiaHistorica({ cartografia_historica }) {
  const [selected, setSelected] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState("antiguos");
  const [viewMode, setViewMode] = useState("grid");
  const [filtersOpen, setFiltersOpen] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return !window.matchMedia("(max-width: 720px)").matches;
  });
  const filtersAsideRef = useRef(null);
  const cardGridRef = useRef(null);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(min-width: 721px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(min-width: 721px)");
    const onChange = () => setIsDesktopLayout(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const truncateText = (text, max = 80) => {
    const t = String(text ?? "");
    return t.length > max ? t.substring(0, max) + "..." : t;
  };

  const filteredSorted = useMemo(() => {
    const q = String(searchQuery || "")
      .trim()
      .toLowerCase();
    const base = Array.isArray(cartografia_historica)
      ? cartografia_historica
      : [];
    const filtered = !q
      ? base
      : base.filter((c) => {
          const title = String(
            c?.title ?? c?.nombre_imagen ?? "",
          ).toLowerCase();
          return title.includes(q);
        });

    const withIndex = filtered.map((c, idx) => ({ ...c, __idx: idx }));

    const byTitle = (a, b) =>
      String(a?.title ?? a?.nombre_imagen ?? "")
        .trim()
        .localeCompare(
          String(b?.title ?? b?.nombre_imagen ?? "").trim(),
          "es",
          {
            sensitivity: "base",
          },
        );

    if (sortKey === "az") return [...withIndex].sort(byTitle);
    if (sortKey === "za") return [...withIndex].sort((a, b) => byTitle(b, a));

    const pickDate = (x) =>
      x?.fecha ??
      x?.created_at ??
      x?.createdAt ??
      x?.updated_at ??
      x?.updatedAt ??
      null;

    const hasAnyDate = withIndex.some((x) => pickDate(x));
    if (!hasAnyDate) return withIndex;

    return [...withIndex].sort((a, b) => {
      const da = new Date(pickDate(a) ?? 0).getTime();
      const db = new Date(pickDate(b) ?? 0).getTime();
      if (Number.isNaN(da) && Number.isNaN(db)) return a.__idx - b.__idx;
      if (Number.isNaN(da)) return 1;
      if (Number.isNaN(db)) return -1;
      return da - db;
    });
  }, [cartografia_historica, searchQuery, sortKey]);

  useSgcCardFiltersPanelHeight(
    cardGridRef,
    filtersAsideRef,
    isDesktopLayout && filtersOpen && filteredSorted.length > 0,
    filteredSorted.length,
    viewMode,
    sortKey,
  );

  const hasCartografia = (cartografia_historica || []).length > 0;

  const clearFilters = () => {
    setSearchQuery("");
    setSortKey("antiguos");
    setViewMode("grid");
  };

  if (!hasCartografia) {
    return (
      <p style={{ textAlign: "center", marginTop: "1rem" }}>
        No hay datos para mostrar en Cartografía Histórica.
      </p>
    );
  }

  return (
    <>
      <div
        className={`sgc-section-shell sgc-section-shell--card-filters${filtersOpen ? "" : " is-filters-collapsed"}`}
      >
        {filtersOpen ? (
          <aside
            ref={filtersAsideRef}
            className="sgc-filtros sgc-filtros--section-sidebar"
            aria-label="Filtros"
          >
            <SgcFiltrosHeader onCollapse={() => setFiltersOpen(false)} />

            <div className="sgc-filtros__body">
              <SgcFiltroSection icon="search" label="Búsqueda">
                <label
                  className="sgc-filtro-search"
                  htmlFor="cartografia-search"
                >
                  <i className="bi bi-search" aria-hidden />
                  <input
                    id="cartografia-search"
                    type="search"
                    placeholder="Buscar recursos…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                    spellCheck="false"
                  />
                </label>
              </SgcFiltroSection>

              <SgcFiltroSection icon="sort-down" label="Ordenar por">
                <select
                  className="sgc-filtro-select"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                >
                  <option value="antiguos">Más antiguos</option>
                  <option value="az">A - Z</option>
                  <option value="za">Z - A</option>
                </select>
              </SgcFiltroSection>

              <SgcFiltroSection icon="grid-3x3-gap" label="Vista">
                <div
                  className="sgc-filtro-view"
                  role="group"
                  aria-label="Modo de vista"
                >
                  <button
                    type="button"
                    className={viewMode === "grid" ? "is-active" : ""}
                    onClick={() => setViewMode("grid")}
                    data-sgc-tooltip="Vista de cuadrícula"
                    aria-label="Vista de cuadrícula"
                  >
                    <i className="bi bi-grid-3x3-gap" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className={viewMode === "list" ? "is-active" : ""}
                    onClick={() => setViewMode("list")}
                    data-sgc-tooltip="Vista de lista"
                    aria-label="Vista de lista"
                  >
                    <i className="bi bi-list" aria-hidden />
                  </button>
                </div>
              </SgcFiltroSection>
            </div>

            <SgcFiltrosFooter
              summary={
                <>
                  Mostrando <strong>{filteredSorted.length}</strong> recursos
                </>
              }
              onClear={clearFilters}
            />
          </aside>
        ) : null}

        <div className="sgc-section-content sgc-filtros-main">
          <div className="sgc-section-title">
            <div className="sgc-section-title__main">
              <SectionTitleIcon sectionKey="cartografia_historica" />
              <div className="sgc-section-title__text">
                <div className="sgc-section-title__h">
                  Cartografía histórica
                </div>
                <div className="sgc-section-title__p">
                  Explora mapas, planos y documentos históricos relacionados con
                  el volcán.
                </div>
              </div>
            </div>
            {!filtersOpen ? (
              <button
                type="button"
                className="sgc-filtros-open"
                onClick={() => setFiltersOpen(true)}
                aria-label="Expandir filtros"
                data-sgc-tooltip="Mostrar filtros"
              >
                <i className="bi bi-funnel" aria-hidden /> <span>Filtros</span>
              </button>
            ) : null}
          </div>

          {filteredSorted.length > 0 ? (
            <div
              ref={cardGridRef}
              className={`cartografia-grid${viewMode === "list" ? " cartografia-grid--list" : ""}`}
            >
              {filteredSorted.map((c, i) => (
                <div
                  key={i}
                  className="cartografia-card"
                  onClick={() => setSelected(c)}
                >
                  {(() => {
                    const { preview } = getImagenUrls(c?.imagen_cartografia);
                    return (
                      <img
                        src={preview ? getMediaUrl(preview) : ""}
                        alt={
                          c.title || c.nombre_imagen || "Cartografía histórica"
                        }
                        loading="lazy"
                        decoding="async"
                        fetchpriority="low"
                      />
                    );
                  })()}
                  <div className="cartografia-card-content">
                    <p>{truncateText(c.title)}</p>
                    <div className="botones">
                      {(() => {
                        const { original } = getImagenUrls(
                          c?.imagen_cartografia,
                        );
                        return original ? (
                          <a
                            href={getMediaUrl(original)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="png"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="bi bi-download"></i>
                            <span> PNG</span>
                          </a>
                        ) : null;
                      })()}

                      {c.archivo_kmz && (
                        <a
                          href={getMediaUrl(c.archivo_kmz)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="kmz"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="bi bi-download"></i>
                          <span> KMZ</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: "center", marginTop: "1rem" }}>
              No se encontraron recursos con los filtros aplicados.
            </p>
          )}
        </div>
      </div>

      {selected && (
        <div
          className="cartografia-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelected(null)}
        >
          <div
            className="cartografia-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="cartografia-modal-close"
              onClick={() => setSelected(null)}
              aria-label="Cerrar"
            >
              ×
            </button>

            <div className="cartografia-modal-header">
              <h3 className="seccion-titulo cartografia-modal-title">
                {selected.title ||
                  selected.nombre_imagen ||
                  "Cartografía histórica"}
              </h3>
            </div>

            <div className="cartografia-modal-body">
              {(() => {
                const { original } = getImagenUrls(
                  selected?.imagen_cartografia,
                );
                return original ? (
                  <div className="cartografia-modal-image">
                    <img
                      src={getMediaUrl(original)}
                      alt={
                        selected.title ||
                        selected.nombre_imagen ||
                        "Cartografía histórica"
                      }
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                ) : null;
              })()}

              {selected?.referencia_bibliografica ? (
                <div className="cartografia-modal-description">
                  {selected.referencia_bibliografica}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
